import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { Check, ChevronsUpDown, Building2, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * Optional Organization picker (Phase 1).
 * - Searchable list of existing Organizations (true + legacy opportunity-derived).
 * - "No organization" clears the selection (Contact → Organization is optional).
 * - Inline create of a new Organization when the typed name has no match.
 *
 * Props:
 *   value        - selected organization id (account_id) | ''
 *   valueName    - selected organization display name (for initial render)
 *   onChange(id, name)
 */
export default function OrganizationCombobox({ value, valueName, onChange, disabled }) {
  const { get, post } = useApi();
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const selectedName = valueName || orgs.find((o) => o.id === value)?.name || '';

  const fetchOrgs = useCallback(async (search) => {
    setLoading(true);
    try {
      const data = await get('/organizations', search ? { search } : undefined);
      setOrgs(data || []);
    } catch (e) {
      console.error('Failed to load organizations:', e);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    if (open) fetchOrgs('');
  }, [open, fetchOrgs]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchOrgs(query), 250);
    return () => clearTimeout(t);
  }, [query, open, fetchOrgs]);

  const handleSelect = (org) => {
    onChange(org.id, org.name);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('', '');
    setOpen(false);
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await post('/organizations', { name });
      toast.success(`Organization "${name}" created`);
      onChange(created.id, created.name);
      setOpen(false);
      setQuery('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const exactMatch = orgs.some(
    (o) => o.name.trim().toLowerCase() === query.trim().toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between rounded-sm font-normal"
          data-testid="contact-organization-trigger"
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            {selectedName
              ? <span className="truncate">{selectedName}</span>
              : <span className="text-muted-foreground">No organization</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type to create..."
            value={query}
            onValueChange={setQuery}
            data-testid="contact-organization-search"
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandGroup>
                  <CommandItem
                    value="__none__"
                    onSelect={handleClear}
                    data-testid="contact-organization-none"
                  >
                    <X className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-40')} />
                    No organization
                  </CommandItem>
                  {orgs.map((org) => (
                    <CommandItem
                      key={org.id}
                      value={org.id}
                      onSelect={() => handleSelect(org)}
                      data-testid={`contact-organization-option-${org.id}`}
                    >
                      <Check className={cn('mr-2 h-4 w-4', value === org.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{org.name}</span>
                      {org.is_legacy && (
                        <span className="ml-auto text-[10px] text-muted-foreground">from opp</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {query.trim() && !exactMatch && (
                  <CommandGroup>
                    <CommandItem
                      value={`__create__${query}`}
                      onSelect={handleCreate}
                      disabled={creating}
                      data-testid="contact-organization-create"
                    >
                      {creating
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Plus className="mr-2 h-4 w-4" />}
                      Create "{query.trim()}"
                    </CommandItem>
                  </CommandGroup>
                )}
                {!query.trim() && orgs.length === 0 && (
                  <CommandEmpty>No organizations yet.</CommandEmpty>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
